/**
 * Anfrage an Lieferanten — aus dem Projekt, eine Mail je Lieferant.
 *
 * Benjamin, 25.09.2026: „Aus einem Projekt heraus sollen sehr einfach
 * Anfragemails erstellt werden können … nutze dafür den wiederverwendbaren
 * Maildialog." Entschieden: nur Anfragen, Stand je Material mit Wiedervorlage,
 * Kommission, Preisvergleich bei mehreren Lieferanten.
 *
 * Zwei Schritte in einem Fenster:
 * 1. **Zusammenstellen** — welche Materialien, bei wem. Vorausgewählt ist je
 *    Material sein Lieferant; weitere, die es führen, lassen sich dazutippen
 *    (dann kommen mehrere Angebote zum Vergleich). Mengen gleich hier.
 * 2. **Mails** — links die Lieferanten, rechts das gewohnte Schreibfenster
 *    (`PostSchreiben`, eingebettet) mit fertigem Text und einer Prüfliste, die
 *    Fehlendes an Ort und Stelle beheben lässt. Abgeschickt wird von Hand.
 */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { fehlerText } from "@vimaana/ui";

import {
  useAnfrageEntwurf, useLieferantAendern, useLieferantenUebersicht, useProjektMaterial, useProjektMaterialAendern,
  type AnfrageEmpfang, type AnfragePruefung, type MaterialAnbieter, type ProjektMaterialDaten, type ProjektMaterialZeile,
} from "../../hooks/useLieferanten";
import LieferantBlatt from "../lieferanten/LieferantBlatt";
import LieferantSuche from "../lieferanten/LieferantSuche";
import PostSchreiben from "./PostSchreiben";
import "../../styles/lieferanten.css";
import "../../styles/anfrage.css";

export type AnfrageStart = {
  lieferantId?: string;
  pmIds?: string[];
  /** Arbeitsplan: Wunschtermin = Datum dieses Tages statt des nächsten Termins. */
  terminId?: string;
};

const tag = (iso: string) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "");

/** Wer ein Material führt — dazu der Lieferant, der im Projekt gilt, falls er nicht in der Liste steht. */
function anbieterVon(m: ProjektMaterialZeile, d: ProjektMaterialDaten): MaterialAnbieter[] {
  const liste = [...m.anbieter];
  if (m.lieferant_id && !liste.some((a) => a.id === m.lieferant_id)) {
    const g = d.gruppen.find((x) => x.lieferant_id === m.lieferant_id);
    liste.unshift({ id: m.lieferant_id, name: m.lieferant_name, preis_netto: null, artikelnummer: m.artikelnummer, standard: false, anfrage_an: g?.anfrage_an ?? null });
  }
  return liste;
}

/** Was zu Beginn angehakt ist — auch Material ohne Lieferant: der wird erst bei der Anfrage bestimmt. */
function startDrin(d: ProjektMaterialDaten, start: AnfrageStart): string[] {
  if (start.pmIds) return d.materialien.filter((m) => start.pmIds!.includes(m.id)).map((m) => m.id);
  if (start.lieferantId) return d.materialien.filter((m) => m.lieferant_id === start.lieferantId).map((m) => m.id);
  return d.materialien
    .filter((m) => !(m.anfragen.some((a) => a.status !== "abgesagt") || m.bestell_status !== "" || !!m.geliefert_am))
    .map((m) => m.id);
}

/** Erste Auswahl: je Material sein Lieferant — was schon angefragt, bestellt oder geliefert ist, bleibt aus. */
function startWahl(d: ProjektMaterialDaten, start: AnfrageStart): Record<string, string[]> {
  const w: Record<string, string[]> = {};
  for (const m of d.materialien) {
    if (start.pmIds) {
      if (start.pmIds.includes(m.id) && m.lieferant_id) w[m.id] = [m.lieferant_id];
      continue;
    }
    if (start.lieferantId) {
      if (m.lieferant_id === start.lieferantId) w[m.id] = [start.lieferantId];
      continue;
    }
    const erledigt = m.anfragen.some((a) => a.status !== "abgesagt") || m.bestell_status !== "" || !!m.geliefert_am;
    if (!erledigt && m.lieferant_id) w[m.id] = [m.lieferant_id];
  }
  return w;
}

export default function AnfrageDialog({ projektId, start, onClose }: { projektId: string; start: AnfrageStart; onClose: () => void }) {
  const daten = useProjektMaterial(projektId);
  const d = daten.data;
  return createPortal(
    <div className="vg-overlay an-dialog" role="dialog" aria-modal="true" aria-label="Anfrage an Lieferanten">
      <button type="button" className="vg-overlay-backdrop" aria-label="Schließen" onClick={onClose} />
      <div className="vg-overlay-panel an-panel">
        {!d ? <p className="lf-leer">Lade …</p> : <Inhalt d={d} projektId={projektId} start={start} onClose={onClose} />}
      </div>
    </div>,
    document.body,
  );
}

function Inhalt({ d, projektId, start, onClose }: { d: ProjektMaterialDaten; projektId: string; start: AnfrageStart; onClose: () => void }) {
  const [wahl, setWahl] = useState<Record<string, string[]>>(() => startWahl(d, start));
  /* Gewählte Lieferanten — auch solche, denen noch nichts zugeordnet ist. */
  const [an, setAn] = useState<string[]>(() => Array.from(new Set(Object.values(startWahl(d, start)).flat())));
  const [schritt, setSchritt] = useState<"auswahl" | "mails">("auswahl");
  const [aktiv, setAktiv] = useState("");
  const [gesendet, setGesendet] = useState<Record<string, string>>({});
  const [hinweise, setHinweise] = useState<Record<string, string>>({});

  // Empfänger-Güte je Lieferant: aus den Gruppen und aus den Anbietern der Materialien.
  const empfang = useMemo(() => {
    const e: Record<string, { name: string; an: AnfrageEmpfang | null }> = {};
    for (const g of d.gruppen) e[g.lieferant_id] = { name: g.name, an: g.anfrage_an };
    for (const m of d.materialien) for (const a of m.anbieter) if (!e[a.id]) e[a.id] = { name: a.name, an: a.anfrage_an };
    return e;
  }, [d]);

  /* Lieferanten, die erst mit dieser Anfrage ins Projekt kommen, kennt nur die Übersicht. */
  const uebersicht = useLieferantenUebersicht(an.some((id) => !empfang[id]));
  const name = (fid: string) => empfang[fid]?.name || uebersicht.data?.lieferanten.find((l) => l.id === fid)?.name || "Lieferant";

  const lieferanten = useMemo(() => {
    const je: Record<string, string[]> = {};
    for (const m of d.materialien) for (const fid of wahl[m.id] ?? []) (je[fid] ||= []).push(m.id);
    return Object.entries(je)
      .map(([id, pmIds]) => ({ id, pmIds, name: name(id) }))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [wahl, d, empfang, uebersicht.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const weiter = () => {
    const offen = lieferanten.find((l) => !gesendet[l.id] && l.id !== aktiv);
    if (offen) setAktiv(offen.id);
    else setAktiv("");
  };
  const alleGesendet = lieferanten.length > 0 && lieferanten.every((l) => gesendet[l.id]);

  return (
    <>
      <div className="vg-overlay-head an-kopf">
        <div>
          <p className="an-ueber">{d.projekt.title} · Kommission {d.kommission}</p>
          <h2 className="vg-overlay-title">{schritt === "auswahl" ? "Anfrage an Lieferanten" : alleGesendet ? "Anfragen verschickt" : "Mails prüfen und senden"}</h2>
        </div>
        <button type="button" className="vg-overlay-close" aria-label="Schließen" onClick={onClose}>×</button>
      </div>

      {schritt === "auswahl" ? (
        <Auswahl d={d} start={start} wahl={wahl} setWahl={setWahl} an={an} setAn={setAn} projektId={projektId} empfang={empfang} name={name}
          lieferanten={lieferanten} onAbbruch={onClose}
          onWeiter={() => { setSchritt("mails"); setAktiv(lieferanten[0]?.id || ""); }} />
      ) : alleGesendet ? (
        <Fertig d={d} lieferanten={lieferanten} gesendet={gesendet} hinweise={Object.values(hinweise)} onClose={onClose} />
      ) : (
        <div className="an-mails">
          <nav className="an-nav" aria-label="Lieferanten">
            <button type="button" className="lf-knopf an-zurueck" onClick={() => setSchritt("auswahl")}>‹ Auswahl</button>
            {lieferanten.map((l) => {
              const an = empfang[l.id]?.an;
              const ohne = !!empfang[l.id] && !an?.email;
              const zeichen = gesendet[l.id] ? "✓" : ohne ? "!" : an?.guete === "rechnung" ? "?" : "";
              return (
                <button key={l.id} type="button" aria-current={aktiv === l.id}
                  className={"an-nav-knopf" + (aktiv === l.id ? " ist-an" : "") + (gesendet[l.id] ? " ist-gesendet" : "") + (!gesendet[l.id] && ohne ? " ist-warnung" : "")}
                  onClick={() => setAktiv(l.id)}>
                  <span className="an-nav-name">{l.name}</span>
                  <span className="an-nav-info">{l.pmIds.length} {l.pmIds.length === 1 ? "Position" : "Positionen"}{zeichen && <b className="an-nav-zeichen">{zeichen}</b>}</span>
                </button>
              );
            })}
          </nav>
          <div className="an-mail">
            {aktiv && lieferanten.find((l) => l.id === aktiv) ? (
              gesendet[aktiv] ? (
                <div className="vg-schreib-fertig">
                  <p>Die Anfrage an <strong>{name(aktiv)}</strong> ist verschickt.</p>
                  {hinweise[aktiv] && <p className="vg-post-fehler">{hinweise[aktiv]}</p>}
                  <button type="button" className="vg-btn vg-btn-primary" onClick={weiter}>{alleGesendet ? "Fertig" : "Weiter zum nächsten Lieferanten"}</button>
                </div>
              ) : (
                <MailSchritt key={aktiv} projektId={projektId} lieferantId={aktiv}
                  pmIds={lieferanten.find((l) => l.id === aktiv)!.pmIds}
                  terminId={start.terminId}
                  letzter={lieferanten.filter((l) => !gesendet[l.id]).length <= 1}
                  onGesendet={(anfrageId, hinweis) => {
                    setGesendet((g) => ({ ...g, [aktiv]: anfrageId || "ja" }));
                    if (hinweis) setHinweise((h) => ({ ...h, [aktiv]: hinweis }));
                  }}
                  onWeiter={weiter} />
              )
            ) : (
              <p className="lf-leer">Links einen Lieferanten wählen.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Schritt 1: Zusammenstellen ───────────────────────────────────────────
//
// Bedarfsorientiert (Benjamin, 03.10.2026): erst „Was", dann „An wen", dann „Wer
// bekommt was". Wer gewählt wird, bekommt automatisch alles, was er schon führt;
// was er noch nicht führt, hakt man an — beim Senden landet es in seiner Liste,
// und der Lieferant gehört zum Projekt (material_anfrage.nach_senden). Ein Preis
// aus der Antwort pflegt den Katalog (position_aendern).

function Auswahl({ d, start, wahl, setWahl, an, setAn, projektId, empfang, name, lieferanten, onWeiter, onAbbruch }: {
  d: ProjektMaterialDaten; start: AnfrageStart; name: (fid: string) => string; wahl: Record<string, string[]>; setWahl: (f: (w: Record<string, string[]>) => Record<string, string[]>) => void;
  an: string[]; setAn: (f: (a: string[]) => string[]) => void;
  projektId: string; empfang: Record<string, { name: string; an: AnfrageEmpfang | null }>;
  lieferanten: { id: string; pmIds: string[]; name: string }[]; onWeiter: () => void; onAbbruch: () => void;
}) {
  const [suche, setSuche] = useState(false);
  const [drin, setDrin] = useState<string[]>(() => startDrin(d, start));
  const ohneAdresse = lieferanten.filter((l) => empfang[l.id] && !empfang[l.id].an?.email);
  const fuehrt = (m: ProjektMaterialZeile, fid: string) => anbieterVon(m, d).some((a) => a.id === fid);
  const materialien = d.materialien;
  const drinM = materialien.filter((m) => drin.includes(m.id));

  // Wer in Frage kommt: Lieferanten des Projekts und alle, die eines der Materialien führen.
  const kandidaten = useMemo(() => {
    const ids = new Set<string>([...d.gruppen.map((g) => g.lieferant_id), ...an]);
    for (const m of materialien) for (const a of anbieterVon(m, d)) ids.add(a.id);
    return [...ids].map((id) => ({ id, name: name(id), zahl: drinM.filter((m) => fuehrt(m, id)).length }))
      .sort((a, b) => b.zahl - a.zahl || a.name.localeCompare(b.name, "de"));
  }, [d, an, drin, name]); // eslint-disable-line react-hooks/exhaustive-deps

  const setzen = (pmId: string, fid: string, ja: boolean) => setWahl((w) => {
    const jetzt = w[pmId] ?? [];
    const neu = ja ? Array.from(new Set([...jetzt, fid])) : jetzt.filter((x) => x !== fid);
    const r = { ...w };
    if (neu.length) r[pmId] = neu; else delete r[pmId];
    return r;
  });
  const materialUmschalten = (m: ProjektMaterialZeile, ja: boolean) => {
    setDrin((x) => (ja ? [...x, m.id] : x.filter((y) => y !== m.id)));
    if (ja) for (const fid of an) { if (fuehrt(m, fid)) setzen(m.id, fid, true); }
    else setWahl((w) => { const r = { ...w }; delete r[m.id]; return r; });
  };
  const lieferantUmschalten = (fid: string) => {
    if (an.includes(fid)) {
      setAn((a) => a.filter((x) => x !== fid));
      setWahl((w) => Object.fromEntries(Object.entries(w).map(([k, v]) => [k, v.filter((x) => x !== fid)]).filter(([, v]) => v.length)));
    } else {
      setAn((a) => [...a, fid]);
      for (const m of drinM) if (fuehrt(m, fid)) setzen(m.id, fid, true);
    }
  };
  const ohneLieferant = drinM.filter((m) => !(wahl[m.id] ?? []).length);

  if (materialien.length === 0) {
    return (
      <div className="an-inhalt">
        <p className="lf-leer">Am Projekt steht noch kein Material. Erst „Aus Angebot übernehmen“ oder „＋ Material“, dann anfragen.</p>
        <div className="lf-knoepfe"><button type="button" className="lf-knopf" onClick={onAbbruch}>Schließen</button></div>
      </div>
    );
  }
  return (
    <>
      <div className="an-inhalt an-schritte">
        <section className="an-schritt">
          <h3 className="an-schritt-titel"><span>1</span> Was wird angefragt?</h3>
          <ul className="lf-liste">
            {materialien.map((m) => {
              const frueher = m.anfragen.filter((a) => a.status !== "nicht_gewaehlt");
              const ja = drin.includes(m.id);
              return (
                <li key={m.id} className={"an-zeile" + (ja ? " ist-an" : "")}>
                  <div className="an-zeile-kopf">
                    <label className="an-haken">
                      <input type="checkbox" checked={ja} onChange={(e) => materialUmschalten(m, e.target.checked)} />
                      <span className="lf-zeile-haupt">
                        <span className="lf-zeile-name">{m.name}{m.art === "maschine" && <em className="lf-art">Maschine</em>}</span>
                        {frueher.length > 0 && (
                          <span className="lf-zeile-info an-frueher">
                            schon angefragt: {frueher.map((a) => `${a.lieferant_name} ${tag(a.gesendet_am)}${a.status === "abgesagt" ? " (abgesagt)" : ""}`).join(", ")}
                          </span>
                        )}
                      </span>
                    </label>
                    <MengeFeld m={m} projektId={projektId} />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="an-schritt">
          <h3 className="an-schritt-titel"><span>2</span> An wen?</h3>
          <div className="an-anbieter an-kandidaten" role="group" aria-label="Lieferanten wählen">
            {kandidaten.map((k) => {
              const e = empfang[k.id]?.an;
              const gewaehlt = an.includes(k.id);
              return (
                <button key={k.id} type="button" aria-pressed={gewaehlt}
                  className={"vg-chip-knopf an-chip" + (gewaehlt ? " is-active" : "") + (empfang[k.id] && !e?.email ? " ist-ohne" : "")}
                  title={empfang[k.id] && !e?.email ? (e?.hinweis || "Keine Adresse für Anfragen") : `führt ${k.zahl} der gewählten Materialien`}
                  onClick={() => lieferantUmschalten(k.id)}>
                  {gewaehlt ? "✓ " : ""}{k.name}{drinM.length ? ` · führt ${k.zahl}/${drinM.length}` : ""}
                </button>
              );
            })}
            {!suche && <button type="button" className="vg-chip-knopf an-chip ist-dazu" onClick={() => setSuche(true)}>＋ Lieferant suchen</button>}
          </div>
          {suche && (
            <div className="lf-zeile-unter">
              <LieferantSuche ausser={kandidaten.map((k) => k.id)} onAbbruch={() => setSuche(false)}
                hinweis="Er wird für diese Anfrage gewählt; welche Materialien er bekommt, hakst du darunter an."
                onWahl={(fid) => { setAn((a) => Array.from(new Set([...a, fid]))); setSuche(false); }} />
            </div>
          )}
        </section>

        {an.length > 0 && drinM.length > 0 && (
          <section className="an-schritt">
            <h3 className="an-schritt-titel"><span>3</span> Wer bekommt was?</h3>
            <div className="an-karten">
              {an.map((fid) => {
                const zahl = drinM.filter((m) => (wahl[m.id] ?? []).includes(fid)).length;
                return (
                  <div key={fid} className="an-karte">
                    <div className="an-karte-kopf">
                      <strong>{name(fid)}</strong>
                      <span className="an-karte-zahl">{zahl}/{drinM.length}</span>
                      <button type="button" className="lf-knopf an-karte-alle" disabled={zahl === drinM.length}
                        onClick={() => { for (const m of drinM) setzen(m.id, fid, true); }}>Alle</button>
                      <button type="button" className="an-karte-weg" aria-label={`${name(fid)} abwählen`} onClick={() => lieferantUmschalten(fid)}>×</button>
                    </div>
                    {empfang[fid] && !empfang[fid].an?.email && <p className="an-warnung">Keine Anfrage-Adresse — lässt sich im nächsten Schritt ergänzen.</p>}
                    <ul className="an-karte-liste">
                      {drinM.map((m) => (
                        <li key={m.id}>
                          <label className="an-haken">
                            <input type="checkbox" checked={(wahl[m.id] ?? []).includes(fid)} onChange={(e) => setzen(m.id, fid, e.target.checked)} />
                            <span>
                              {m.name}
                              {!fuehrt(m, fid) && <em className="an-neu" title="Kommt beim Senden in seine Materialliste">neu für ihn</em>}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
      <div className="an-fuss">
        <div className="an-summe">
          {ohneLieferant.length > 0 && <span className="an-warnung">Noch bei keinem Lieferanten: {ohneLieferant.map((m) => m.name).join(", ")}. </span>}
          {lieferanten.length === 0 ? (!drinM.length ? "Erst unter 1 Material wählen." : !an.length ? "Unter 2 wählen, wer angefragt wird." : "Den gewählten Lieferanten unter 3 Materialien zuordnen.") : (
            <>
              {lieferanten.length} {lieferanten.length === 1 ? "Mail" : "Mails"}: {lieferanten.map((l) => `${l.name} (${l.pmIds.length})`).join(", ")}
              {ohneAdresse.length > 0 && <span className="an-warnung"> · ohne Anfrage-Adresse: {ohneAdresse.map((l) => l.name).join(", ")}</span>}
              {an.some((fid) => !lieferanten.some((l) => l.id === fid)) && (
                <span> · ohne Material, bekommt keine Mail: {an.filter((fid) => !lieferanten.some((l) => l.id === fid)).map(name).join(", ")}</span>
              )}
            </>
          )}
        </div>
        <div className="lf-knoepfe">
          <button type="button" className="lf-knopf" onClick={onAbbruch}>Abbrechen</button>
          <button type="button" className="lf-knopf is-primaer" disabled={lieferanten.length === 0} onClick={onWeiter}>
            Weiter: {lieferanten.length || ""} {lieferanten.length === 1 ? "Mail" : "Mails"} prüfen
          </button>
        </div>
      </div>
    </>
  );
}

/** Menge direkt im Fenster — gespeichert beim Verlassen des Felds. */
function MengeFeld({ m, projektId }: { m: ProjektMaterialZeile; projektId: string }) {
  const a = useProjektMaterialAendern(projektId);
  const [menge, setMenge] = useState(m.menge);
  const speichern = () => { if (menge.trim() !== m.menge.trim()) a.aendern.mutate({ id: m.id, menge: menge.trim() }); };
  return (
    <label className={"an-menge" + (!menge.trim() ? " ist-leer" : "")}>
      <input className="lf-feld" inputMode="decimal" value={menge} placeholder="Menge" aria-label={`Menge ${m.name}`}
        onChange={(e) => setMenge(e.target.value)} onBlur={speichern}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      <span>{m.einheit || "—"}</span>
    </label>
  );
}

// ── Schritt 2: je Lieferant die Mail ─────────────────────────────────────

function MailSchritt({ projektId, lieferantId, pmIds, terminId, letzter, onGesendet, onWeiter }: {
  projektId: string; lieferantId: string; pmIds: string[]; terminId?: string; letzter: boolean;
  onGesendet: (anfrageId: string, hinweis: string) => void; onWeiter: () => void;
}) {
  const e = useAnfrageEntwurf(projektId, lieferantId, pmIds, terminId);
  const [blatt, setBlatt] = useState(false);
  if (e.isPending) return <p className="lf-leer">Kontor schreibt die Anfrage …</p>;
  if (e.isError || !e.data) return <p className="lf-fehler">{fehlerText(e.error)}</p>;
  const x = e.data;
  return (
    <>
      <PostSchreiben
        key={e.dataUpdatedAt}
        eingebettet
        onClose={onWeiter}
        projektId={projektId}
        lieferantId={lieferantId}
        ziele={x.ziele}
        an={x.an}
        betreff={x.betreff}
        text={x.text}
        titel={`Anfrage an ${x.lieferant_name}`}
        anfrage={x.pm_ids}
        onGesendet={(r) => onGesendet(r.anfrage_id || "", r.hinweis || "")}
        fertigText={letzter ? "Fertig" : "Weiter zum nächsten Lieferanten"}
        oben={x.pruefung.length > 0 ? <Pruefliste punkte={x.pruefung} projektId={projektId} lieferantId={lieferantId} onKontakt={() => setBlatt(true)} /> : null}
      />
      {blatt && <LieferantBlatt lieferantId={lieferantId} onClose={() => setBlatt(false)} />}
    </>
  );
}

function Pruefliste({ punkte, projektId, lieferantId, onKontakt }: {
  punkte: AnfragePruefung[]; projektId: string; lieferantId: string; onKontakt: () => void;
}) {
  const daten = useProjektMaterial(projektId);
  return (
    <div className="an-pruefung" aria-label="Vor dem Senden">
      <p className="an-pruefung-titel">Vor dem Senden <small>— Beheben erstellt den Text neu</small></p>
      <ul>
        {punkte.map((p, i) => {
          const pmId = p.aktion.startsWith("menge:") ? p.aktion.slice(6) : "";
          const m = pmId ? daten.data?.materialien.find((x) => x.id === pmId) : undefined;
          return (
            <li key={i} className={"an-punkt ist-" + p.stufe}>
              <span className="an-punkt-text">{p.text}</span>
              {p.aktion === "kontakt" && <button type="button" className="lf-knopf" onClick={onKontakt}>Ansprechpartner ergänzen</button>}
              {m && <MengeFeld m={m} projektId={projektId} />}
              {p.aktion === "kundennummer" && <KundennummerFeld lieferantId={lieferantId} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function KundennummerFeld({ lieferantId }: { lieferantId: string }) {
  const a = useLieferantAendern(lieferantId);
  const [nr, setNr] = useState("");
  return (
    <span className="an-kundennr">
      <input className="lf-feld" value={nr} onChange={(e) => setNr(e.target.value)} placeholder="Kundennummer" aria-label="Unsere Kundennummer"
        onKeyDown={(e) => { if (e.key === "Enter" && nr.trim()) a.mutate({ kundennummer: nr.trim() }); }} />
      <button type="button" className="lf-knopf" disabled={!nr.trim() || a.isPending} onClick={() => a.mutate({ kundennummer: nr.trim() })}>Merken</button>
    </span>
  );
}

// ── Fertig ───────────────────────────────────────────────────────────────

function Fertig({ d, lieferanten, gesendet, hinweise, onClose }: {
  d: ProjektMaterialDaten; lieferanten: { id: string; name: string; pmIds: string[] }[];
  gesendet: Record<string, string>; hinweise: string[]; onClose: () => void;
}) {
  const wv = d.anfragen.filter((a) => Object.values(gesendet).includes(a.id)).map((a) => a.wiedervorlage_am).sort()[0] || "";
  return (
    <div className="an-inhalt an-fertig">
      <p>
        {lieferanten.length === 1 ? "Die Anfrage ist" : `Alle ${lieferanten.length} Anfragen sind`} verschickt:{" "}
        {lieferanten.map((l) => l.name).join(", ")}. Die Materialien stehen auf „angefragt“.
      </p>
      {hinweise.map((h, i) => <p key={i} className="vg-post-fehler">{h}</p>)}
      <p className="lf-hinweis">
        Antworten kommen in den Schriftverkehr des Projekts. Preise trägst du am Material ein und vergleichst sie dort.
        {wv && <> Liegt bis {new Date(wv).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })} keine Antwort vor, meldet sich das Projekt.</>}
      </p>
      <div className="lf-knoepfe"><button type="button" className="lf-knopf is-primaer" onClick={onClose}>Schließen</button></div>
    </div>
  );
}
